import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-daisyui';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import useFiduciaryEscalation from '@/api/useFiduciaryEscalation.ts';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import StepFormSubtitle from '@/components/ui/StepFormSubtitle/StepFormSubtitle.tsx';
import StepFormTextarea from '@/components/functional/StepFormTextarea/StepFormTextarea.tsx';
import StepFormTitle from '@/components/ui/StepFormTitle/StepFormTitle.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import type {
  RecommendationEngineData,
  RecommendationEngineSteps,
} from '@/pages/recommendationEngine/recommendationEngineContext.ts';
import { cn } from '@/utils/classNames.ts';
import type { EscalationCategory } from '$/requests/FiduciaryEscalation.ts';

type EscalationFields = {
  escalation_reason: string;
  escalation_category: EscalationCategory;
};

function resolveReviewRationale(d: Partial<RecommendationEngineData> | null): string {
  const filters = d?.governance_result?.compass_filters ?? [];
  const escalated = filters.find((f) => f.status === 'escalation');
  if (escalated?.explanation?.trim()) return escalated.explanation.trim();
  if (d?.governance_result?.governance_helper_copy?.trim()) {
    return d.governance_result.governance_helper_copy.trim();
  }
  return '';
}

const StepFiduciaryEscalation: FC = () => {
  const { t } = useTranslation();
  const { goToStep, setContextData, contextData } = useStepperContext<
    RecommendationEngineSteps,
    RecommendationEngineData
  >();
  const d = contextData as Partial<RecommendationEngineData> | null;
  const { mutate: submitEscalation, isPending, error: escalationError } = useFiduciaryEscalation();

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<EscalationFields>({
    mode: 'onChange',
    defaultValues: {
      escalation_reason: d?.escalation_reason ?? '',
      escalation_category: 'governance_interpretation',
    },
  });

  const reason = watch('escalation_reason');
  const canSubmit = isValid && reason.trim().length > 0;
  const reviewRationale =
    resolveReviewRationale(d) ||
    t('recommendationEnginePage.fiduciary.defaultReviewReason');

  const onSubmit = (data: EscalationFields) => {
    if (!d?.decision_id) return;

    submitEscalation(
      {
        decision_id: d.decision_id,
        escalation_reason: data.escalation_reason.trim(),
        escalation_category: data.escalation_category,
      },
      {
        onSuccess: () => {
          setContextData((prev) => ({
            ...prev,
            escalation_reason: data.escalation_reason.trim(),
            session_outcome: 'escalated',
          }));
          goToStep('session-recorded');
        },
      },
    );
  };

  return (
    <BrandCardSideContent centerContent>
      <form
        className="flex flex-col flex-1 gap-6 w-full max-w-lg mx-auto"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-2">
          <StepFormTitle>{t('recommendationEnginePage.fiduciary.title')}</StepFormTitle>
          <StepFormSubtitle>{t('recommendationEnginePage.fiduciary.subtitle')}</StepFormSubtitle>
        </div>

        <div
          className={cn(
            'rounded-xl border border-[rgba(0,0,0,0.12)] bg-[#F8FAFC] p-4 flex flex-col gap-2',
          )}
        >
          <div className="flex flex-row items-start gap-2">
            <QuestionMarkCircleIcon className="w-5 h-5 shrink-0 text-[#1A5292] mt-0.5" aria-hidden />
            <div className="flex flex-col gap-1">
              <p className="font-dm text-sm font-semibold text-[#1A5292]">
                {t('recommendationEnginePage.fiduciary.whyReviewTitle')}
              </p>
              <p className="font-dm text-sm text-black/75 leading-relaxed">{reviewRationale}</p>
            </div>
          </div>
        </div>

        {escalationError && (
          <Alert status="error" className="shrink-0">
            <span>
              {escalationError instanceof Error
                ? escalationError.message
                : t('recommendationEnginePage.fiduciary.submitFailed')}
            </span>
          </Alert>
        )}

        <div className="flex flex-col gap-2">
          <label className="font-dm text-xs italic text-black/55" htmlFor="escalation_category">
            {t('recommendationEnginePage.fiduciary.categoryLabel')}
          </label>
          <select
            id="escalation_category"
            className="select select-bordered w-full"
            {...register('escalation_category', { required: true })}
          >
            <option value="context_gap">{t('recommendationEnginePage.fiduciary.category.contextGap')}</option>
            <option value="framework_conflict">{t('recommendationEnginePage.fiduciary.category.frameworkConflict')}</option>
            <option value="governance_interpretation">
              {t('recommendationEnginePage.fiduciary.category.governanceInterpretation')}
            </option>
            <option value="other">{t('recommendationEnginePage.fiduciary.category.other')}</option>
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label className="font-dm text-xs italic text-black/55" htmlFor="escalation_reason">
            {t('recommendationEnginePage.fiduciary.reasonLabel')}
          </label>
          <StepFormTextarea
            id="escalation_reason"
            placeholder={t('recommendationEnginePage.fiduciary.typeHere')}
            {...register('escalation_reason', { required: true, minLength: 1 })}
          />
          <p className="font-dm text-xs italic text-black/50">
            {t('recommendationEnginePage.fiduciary.reasonHint')}
          </p>
        </div>

        <LoadingButton
          loading={isPending}
          disabled={!canSubmit}
          color="primary"
          className="w-full mt-auto"
          type="submit"
        >
          {t('recommendationEnginePage.fiduciary.markForReview')}
        </LoadingButton>
      </form>
    </BrandCardSideContent>
  );
};

export default StepFiduciaryEscalation;
